<script setup>
import ArgonButton from "@/components/ArgonButton.vue";
import "flatpickr/dist/flatpickr.css";
import axios from "axios";
import moment from "moment";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
</script>

<template>
  <div class="py-4 container-fluid">
    <div class="card">
      <!-- Title -->
      <div class="card-header pb-0">
        <h6 class="mb-0">{{ gameCode }} {{ gameName }}</h6>
      </div>

      <!-- Filter Form -->
      <div class="row px-3 py-2">
        <form class="w-100" @submit.prevent="searchData">
          <div class="row gx-2 gy-2">
            <div class="col-md-3">
              <label>Start date</label>
              <VueDatePicker v-model="startDate" :enableTimePicker="false" />
            </div>
            <div class="col-md-3">
              <label>End date</label>
              <VueDatePicker v-model="endDate" :enableTimePicker="false" />
            </div>
            <div class="col-md-2">
              <label>Agent</label>
              <select v-model="agent" class="form-control">
                <option value="">-- เลือก Agent --</option>
                <option v-for="a in agentList" :key="a.id" :value="a.id">
                  {{ a.code }}
                </option>
              </select>
            </div>
            <div class="col-md-2">
              <label>Username</label>
              <input
                v-model="username"
                class="form-control"
                placeholder="กรอกชื่อผู้เล่น"
              />
            </div>
            <div class="col-md-2">
              <label>Transaction ID</label>
              <input
                v-model="transaction_id"
                class="form-control"
                placeholder="กรอกชื่อรายการ"
              />
            </div>

            <div class="col-12 d-flex justify-content-center gap-2 mt-3">
              <argon-button
                color="success"
                variant="gradient"
                @click="searchData"
                :disabled="loading"
              >
                <template v-if="loading">กำลังโหลด...</template>
                <template v-else>ค้นหา</template>
              </argon-button>

              <argon-button
                color="danger"
                variant="gradient"
                @click="resetData"
                :disabled="loading"
              >
                <template v-if="loading">กำลังโหลด...</template>
                <template v-else>Reset</template>
              </argon-button>
            </div>
          </div>
        </form>
      </div>

      <!-- Data Table -->
      <div class="card-body px-0 pt-0 pb-2">
        <div class="table-responsive p-2">
          <table class="table text-center" style="font-size: 14px">
            <thead>
              <tr>
                <th>No</th>
                <th>Transaction ID</th>
                <th @click="sortBy('agent')" style="cursor: pointer">
                  Agent
                  <span v-if="sortKey === 'agent'">
                    {{ sortOrder === "asc" ? "▲" : "▼" }}
                  </span>
                </th>
                <th @click="sortBy('username')" style="cursor: pointer">
                  ชื่อผู้เล่น
                  <span v-if="sortKey === 'username'">
                    {{ sortOrder === "asc" ? "▲" : "▼" }}
                  </span>
                </th>
                <th @click="sortBy('updated_date')" style="cursor: pointer">
                  วันที่
                  <span v-if="sortKey === 'updated_date'">
                    {{ sortOrder === "asc" ? "▲" : "▼" }}
                  </span>
                </th>
                <th @click="sortBy('updated_time')" style="cursor: pointer">
                  เวลา
                  <span v-if="sortKey === 'updated_time'">
                    {{ sortOrder === "asc" ? "▲" : "▼" }}
                  </span>
                </th>
                <th @click="sortBy('play_status_id')" style="cursor: pointer">
                  สถานะ
                  <span v-if="sortKey === 'play_status_id'">
                    {{ sortOrder === "asc" ? "▲" : "▼" }}
                  </span>
                </th>
                <th @click="sortBy('currency')" style="cursor: pointer">
                  Currency
                  <span v-if="sortKey === 'currency'">
                    {{ sortOrder === "asc" ? "▲" : "▼" }}
                  </span>
                </th>
                <th @click="sortBy('bet_amount')" style="cursor: pointer">
                  เดิมพัน
                  <span v-if="sortKey === 'bet_amount'">
                    {{ sortOrder === "asc" ? "▲" : "▼" }}
                  </span>
                </th>
                <th @click="sortBy('win_loss')" style="cursor: pointer">
                  ผล
                  <span v-if="sortKey === 'win_loss'">
                    {{ sortOrder === "asc" ? "▲" : "▼" }}
                  </span>
                </th>
                <th @click="sortBy('balance_credit')" style="cursor: pointer">
                  คงเหลือ
                  <span v-if="sortKey === 'balance_credit'">
                    {{ sortOrder === "asc" ? "▲" : "▼" }}
                  </span>
                </th>
                <th>โหมด</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="11">🔄 กำลังโหลดข้อมูล...</td>
              </tr>
              <tr v-else v-for="(item, index) in data" :key="index">
                <td>{{ (currentPage - 1) * rowsPerPage + index + 1 }}</td>
                <td>{{ item.transaction_id }}</td>
                <td>{{ item.agent }}</td>
                <td>{{ item.username }}</td>
                <td>{{ item.updated_date }}</td>
                <td>{{ item.updated_time }}</td>
                <td>
                  {{
                    item.play_status_id === 1
                      ? "ชนะ"
                      : item.play_status_id === 2
                        ? "เสมอ"
                        : "แพ้"
                  }}
                </td>
                <td>{{ item.currency }}</td>
                <td>{{ item.bet_amount }}</td>
                <td>{{ item.win_loss }}</td>
                <td>{{ item.balance_credit }}</td>
                <td>
                  {{
                    item.is_feature_buy === 1
                      ? "ซื้อฟีเจอร์"
                      : item.is_scatter === 1
                        ? "เข้า scatter"
                        : item.is_freespin === 1
                          ? "โบนัส"
                          : "ปกติ"
                  }}
                </td>
              </tr>
              <tr v-if="!loading && data.length === 0">
                <td colspan="11">ไม่มีข้อมูล</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="pagination-wrapper">
          <!-- Desktop / Tablet -->
          <div class="pagination-desktop">
            <div class="d-flex gap-2 align-items-center">
              <argon-button
                color="secondary"
                size="sm"
                @click="goToFirstPage"
                :disabled="currentPage === 1"
              >
                ⏮ หน้าแรก
              </argon-button>

              <argon-button
                color="info"
                size="sm"
                @click="previousPage"
                :disabled="currentPage === 1"
              >
                ◀ ก่อนหน้า
              </argon-button>

              <div class="d-flex align-items-center gap-1 px-2">
                <span>หน้า</span>
                <select
                  v-model.number="currentPage"
                  class="form-select form-select-sm"
                  style="width: 90px"
                  @change="changePage"
                >
                  <option v-for="page in totalPages" :key="page" :value="page">
                    {{ page }}
                  </option>
                </select>
                <span>/ {{ totalPages }}</span>
              </div>

              <argon-button
                color="info"
                size="sm"
                @click="nextPage"
                :disabled="currentPage === totalPages"
              >
                ถัดไป ▶
              </argon-button>

              <argon-button
                color="secondary"
                size="sm"
                @click="goToLastPage"
                :disabled="currentPage === totalPages"
              >
                หน้าสุดท้าย ⏭
              </argon-button>
            </div>

            <p class="m-0">จำนวน {{ totalCount }} รายการ</p>
          </div>

          <!-- Mobile -->
          <div class="pagination-mobile">
            <div class="d-flex gap-2 w-100">
              <argon-button
                class="w-50"
                size="sm"
                color="info"
                @click="previousPage"
                :disabled="currentPage === 1"
              >
                ◀ ก่อนหน้า
              </argon-button>

              <argon-button
                class="w-50"
                size="sm"
                color="info"
                @click="nextPage"
                :disabled="currentPage === totalPages"
              >
                ถัดไป ▶
              </argon-button>
            </div>
            <div class="d-flex gap-2 w-100 mt-2">
              <argon-button
                class="w-50"
                size="sm"
                color="secondary"
                @click="goToFirstPage"
                :disabled="currentPage === 1"
              >
                ⏮ หน้าแรก
              </argon-button>

              <argon-button
                class="w-50"
                size="sm"
                color="secondary"
                @click="goToLastPage"
                :disabled="currentPage === totalPages"
              >
                หน้าสุดท้าย ⏭
              </argon-button>
            </div>

            <select
              v-model.number="currentPage"
              class="form-select form-select-sm mt-2"
              @change="changePage"
            >
              <option v-for="page in totalPages" :key="page" :value="page">
                หน้า {{ page }} / {{ totalPages }}
              </option>
            </select>

            <p class="text-center mt-2 mb-0">จำนวน {{ totalCount }} รายการ</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "GameDetail",
  components: { VueDatePicker },
  // schema (schema_name ของเกม) ส่งมาจาก route param เช่น /game/cat_vs_dog
  props: {
    schema: { type: String, required: true },
  },
  data() {
    return {
      data: [],
      agentList: [],
      startDate: moment().format("YYYY-MM-DD"),
      endDate: moment().format("YYYY-MM-DD"),
      agent: "",
      username: "",
      transaction_id: "",
      loading: false,
      currentPage: 1,
      rowsPerPage: 50,
      totalPages: 1,
      totalCount: 0,
      sortKey: "",
      sortOrder: "asc",
    };
  },
  computed: {
    // ชื่อ/โค้ดเกม แสดงหัวตาราง (ส่งมาทาง query จาก sidebar; fallback เป็น schema)
    gameName() {
      return this.$route.query.name || this.schema;
    },
    gameCode() {
      return this.$route.query.code || "";
    },
  },
  watch: {
    // สลับเกม (เปลี่ยน route param) โดยไม่ unmount → รีเซ็ตแล้วโหลดใหม่
    schema() {
      this.resetData();
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    searchData() {
      this.currentPage = 1;
      this.getData();
    },
    changePage() {
      this.getData();
    },
    goToFirstPage() {
      if (this.currentPage !== 1) {
        this.currentPage = 1;
        this.getData();
      }
    },
    goToLastPage() {
      if (this.currentPage !== this.totalPages) {
        this.currentPage = this.totalPages;
        this.getData();
      }
    },
    sortBy(column) {
      if (this.sortKey === column) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = column;
        this.sortOrder = "asc";
      }

      this.data.sort((a, b) => {
        const valA = a[column];
        const valB = b[column];

        if (valA === null || valA === undefined) return 1;
        if (valB === null || valB === undefined) return -1;

        if (typeof valA === "number" && typeof valB === "number") {
          return this.sortOrder === "asc" ? valA - valB : valB - valA;
        } else {
          return this.sortOrder === "asc"
            ? String(valA).localeCompare(String(valB))
            : String(valB).localeCompare(String(valA));
        }
      });
    },
    async getData() {
      this.loading = true;
      this.startDate = moment(this.startDate).format("YYYY-MM-DD");
      this.endDate = moment(this.endDate).format("YYYY-MM-DD");
      // ⚠️ ชั่วคราว: schema_name ใน DB ไม่ตรงกับ endpoint จริงของ backend
      //    วิธีแก้ถาวร = อัปเดต schema_name ใน game_config ให้ตรง แล้วลบ map นี้ทิ้ง
      const ENDPOINT_OVERRIDE = { neko_shrine_plinko: "neko_plinko" };
      const endpoint = ENDPOINT_OVERRIDE[this.schema] || this.schema;
      try {
        const response = await axios.post(
          `https://dashboard-api.linkstudio.io/api/v1/game/control/${endpoint}/${this.startDate}/${this.endDate}/${this.currentPage}/${this.rowsPerPage}`,
          {
            ...(this.agent && { agent: this.agent.trim() }),
            ...(this.username && { username: this.username.trim() }),
            ...(this.transaction_id && {
              transaction_id: this.transaction_id.trim(),
            }),
          }
        );

        const res = response.data.data;
        this.data = res.list;
        this.agentList = res.agent;
        this.totalPages = res.totalPages;
        this.totalCount = res.count;
        this.rowsPerPage = res.perPage;
        this.currentPage = res.currentPage;
      } catch (error) {
        console.error(error);
        this.$swal({
          icon: "error",
          title: "เกิดข้อผิดพลาด",
          text:
            error.response?.data?.responseStatus?.message ||
            error.message ||
            "ไม่สามารถโหลดข้อมูลได้",
        });
      } finally {
        this.loading = false;
      }
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.getData();
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.getData();
      }
    },
    resetData() {
      this.startDate = moment().format("YYYY-MM-DD");
      this.endDate = moment().format("YYYY-MM-DD");
      this.agent = "";
      this.username = "";
      this.transaction_id = "";
      this.currentPage = 1;
      this.sortKey = "";
      this.sortOrder = "asc";
      this.getData();
    },
  },
};
</script>

<style scoped>
.table td,
.table th {
  vertical-align: middle;
}

th {
  user-select: none;
}

th span {
  margin-left: 5px;
  font-size: 0.8rem;
}

.pagination-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
}

.pagination-desktop {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.pagination-mobile {
  display: none;
  width: 100%;
}

@media (max-width: 768px) {
  .pagination-desktop {
    display: none;
  }

  .pagination-mobile {
    display: block;
  }
}
</style>
